import { ChatUserPermitsEnum, ChatUserRolesEnum } from 'constants/chats';

import { wsMessageTypeEnum } from 'hooks/useWebsocketTypes';
import { PollInMessage } from 'types/pollTypes';
import { FileDescriptor } from './fileTypes';

export type handleDialogClickProps = {
  e?: React.MouseEvent<HTMLElement, MouseEvent>;
  folder?: Folder | null;
};

export enum messageStatusEnum {
  sent = 1,
  delivered = 2,
  read = 3,
}

export enum DialogType {
  chat = 1,
  group = 2,
}

export enum ChatUserType {
  'user' = 0,
  'admin' = 1,
}

export enum ChatContentEnum {
  chat = 0,
  createChat = 1,
  editChat = 2,
}

export enum SearchChatFilters {
  'dialogs' = 21,
  'messages' = 22,
}

export enum ChatActionEnum {
  delete = 'delete',
  edit = 'edit',
  leave = 'leave',
}

export enum ChatFilterEnum {
  all = 'all',
  group = 'group',
  unread = 'unread',
}

export type CustomMenuAction = {
  action(): void;
  disabled?: boolean;
  title: string;
  icon?: JSX.Element;
  class?: string;
};

export type NewNotificationInfo = {
  id: string;
  img: string;
  userName: string;
  dialogId: number;
};

export type ChatUserStatus = {
  isOnline: boolean;
  lastSeen: string | null;
};

export type ChatInterlocutor = {
  userId: string;
  fullName: string;
  nickname: string;
  image: FileDescriptor | null;
  status?: ChatUserStatus;
};

export type SearchChatUser = {
  id: string;
  fullName: string;
  nickname: string;
  image: FileDescriptor;
  email: string;
  isAdmin: boolean;
  status: ChatUserStatus;
};

export type FoundDialogType = {
  createdAt: string;
  dialogId: number;
  dialogType: DialogType;
  fullName: string;
  image: FileDescriptor;
  messageId: number;
  status: ChatUserStatus;
  userId: string;
};

export type FoundMessageType = {
  createdAt: string;
  dialogId: number;
  fullName: string;
  image: FileDescriptor;
  messageId: number;
  status: ChatUserStatus;
  text: string;
  userId: string;
};

export type SearchInDialogQuery = {
  id: number;
  searchField: string;
  sd?: string;
  ed?: string;
  limit?: number;
};

export type ChatUser = {
  userId: string;
  fullName: string;
  nickname: string;
  image: FileDescriptor;
  isAdmin: boolean;
  status: ChatUserStatus;
  role: ChatUserRolesEnum;
};

export type ChatUserParsed = {
  imageUrl?: FileDescriptor['path'];
  initials: string;
  status?: ChatUserStatus;
  userId: ChatInterlocutor['userId'];
  userName: ChatInterlocutor['fullName'];
  role?: ChatUserRolesEnum;
};

export type ChatMessage = {
  id: number;
  text: string | null;
  type: wsMessageTypeEnum;
  files: FileDescriptor[] | null;
  /**
   * Свои исходящие: максимальный статус от других участников (кроме автора).
   * Чужие входящие: статус, который выставил текущий пользователь после просмотра.
   */
  read: messageStatusEnum;
  ownerId: string | null;
  answered?: number;
  ansPreview?: AnswerPreviewType;
  forwarded?: number;
  fwdPreview?: FwdPreviewType;
  createdAt: string;
  poll?: PollInMessage;
};

export type PreviewAuthorType = {
  i: string;
  fn: string;
  im?: string;
};

export type PreviewFileType = {
  fileName: string;
  fileType: number;
  path: string;
};

export type PreviewFileStatsType = {
  ft: number | null;
  c: number;
};

export type ChatPreviewType = {
  cd: string;
  i: number;
  mt: wsMessageTypeEnum;
  o: PreviewAuthorType;
  t?: string;
  ud: string;
  f?: PreviewFileType;
  st?: PreviewFileStatsType[];
};

export interface AnswerPreviewType {
  i: number;
  mt: number;
  o: PreviewAuthorType;
  t: string;
  f?: PreviewFileType;
  st?: PreviewFileStatsType[];
}

export type FwdPreviewType = {
  o: PreviewAuthorType;
};

export type SelectedDialogUserInfo = {
  userId: string;
  fullName: string;
  nickname: string;
  image: FileDescriptor | null;
  isAdmin?: boolean;
  role?: ChatUserRolesEnum;
  status?: ChatUserStatus;
};

export type ChatDescription = {
  id: number;
  name: string | null;
  type: DialogType;
  image: FileDescriptor | null;
  folderNames?: [];
  interlocutor?: ChatInterlocutor;
  isBlocked: boolean;
  isPinned: boolean;
  isUnread?: boolean;
  messageId: number;
  notificationsDisable: boolean;
  unreadMessageCount: number;
  index: number;
  isAdmin?: boolean;
  status?: ChatUserStatus;
  firstMessageId?: number;
  lastReadMessageId?: number;
  isDeleted?: boolean;
  preview?: ChatPreviewType;
  activeCall?: boolean;
  permits?: ChatUserPermitsEnum[];
};

export type PendingChatDescription = Omit<ChatDescription, 'id'> & {
  id?: undefined;
};

export type SelectedChat = ChatDescription | PendingChatDescription;

/** GET /info */
export type SelectedDialogInfo = {
  id: number;
  type: DialogType;
  name?: string | null;
  interlocutor?: SelectedDialogUserInfo;
  image?: FileDescriptor | null;
  users?: SelectedDialogUserInfo[];
  isDeleted?: boolean;
  isCustomNamed?: boolean;
  firstMessageId?: number;
  lastReadMessageId?: number;
  unreadMessageCount?: number;
  isPinned?: boolean;
  isUnread?: boolean;
  isBlocked?: boolean;
  pinnedMessageId?: number;
  pinnedMessages?: ChatPreviewType[];
  activeCall?: boolean;
  permits?: ChatUserPermitsEnum[];
  messageId?: number;
};

export type FolderDialogType = {
  cType: DialogType;
  fullName: string;
  nickname: string;
  id?: number;
  image: FileDescriptor;
};

export type AuthorOfMessageType = {
  imgPath?: string | null;
  name?: string | null;
};

export type Folder = {
  id: number;
  name: string;
  ord: number;
  /* Число диалогов с непрочитанными сообщениями */
  unreadCount: number;
};

export type MediaRefType = {
  recorder: MediaRecorder | null;
  stream: MediaStream | null;
  timeoutId: number | null;
};
